import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  OutlinedInput,
  Checkbox,
  useTheme,
  InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CustomButton from "../../../../components/@extended/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import { openMessage } from "../../../../store/slices/showMessageSlice";
import { bloqcibeApi } from "../../../../store/slices/apiSlice";
import { triggerRefetch } from "../../../../store/slices/querySlice";
import { getStatusColor, getPriorityColor } from "../../../util";

const CreateQueryForm = ({
  onBack,
  sponsorId,
  trialId,
  siteId,
  trialSiteId,
  subjectMasterId,
  crfMasterId,
  stepKey,
  sectionKey,
  subSectionKey,
  categoryKey,
  fieldKey,
  fieldLabel,
  stepName,
  sectionName,
  subSectionName,
  categoryName,
  onQueryCreated,
  users = [],
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [createQuery, { isLoading }] = bloqcibeApi.endpoints.createQuery.useMutation();
  const [uploadAttachments, { isLoading: isUploading }] = bloqcibeApi.endpoints.uploadQueryAttachments.useMutation();
  const { data: latestTagData, isLoading: isLoadingTag } = bloqcibeApi.endpoints.getLatestQueryTag.useQuery(
    { sponsorId: sponsorId, trialId: trialId, siteId: siteId },
    {
      skip: !sponsorId || !trialId || !siteId, // Skip if sponsorId, trialId, or siteId is not available
    }
  );
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  
  // Find self user and initialize default assignee if self is true
  const selfUser = users.find(u => u.self === true);
  const initialAssignees = selfUser ? [selfUser.userId] : [];
  const [selectedAssignees, setSelectedAssignees] = useState(initialAssignees);
  
  const initialFormDataAssignees = selfUser ? [{
    userId: selfUser.userId,
    email: selfUser.id,
    name: selfUser.display,
  }] : [];
  
  const [formData, setFormData] = useState({
    sponsorId: String(sponsorId) || "",
    trialId: trialId || "",
    siteId: siteId || "",
    subjectMasterId: subjectMasterId || "",
    crfMasterId: crfMasterId || "",
    stepKey: stepKey || "",
    sectionKey: sectionKey || "",
    subSectionKey: subSectionKey || "",
    categoryKey: categoryKey || "",
    fieldKey: fieldKey || "",
    title: "",
    description: "",
    priority: "MEDIUM",
    status: "OPEN",
    active: true,
    assignees: initialFormDataAssignees,
    trialSiteId: trialSiteId || "",
    fieldLabel: fieldLabel || "",
    tag: "",
    metaData: "",
  });

  // Fetch and populate tag when component mounts or siteId changes
  useEffect(() => {
    if (latestTagData?.tag) {
      setFormData((prev) => ({
        ...prev,
        tag: latestTagData.tag,
      }));
    }
  }, [latestTagData]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    // Validate file count
    if (files.length + selectedFiles.length > 10) {
      dispatch(
        openMessage({
          message: "Maximum 10 files allowed",
          messageSeverity: "warning",
        })
      );
      return;
    }

    // Validate file types and sizes
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword' // .doc
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes

    const invalidFiles = [];
    files.forEach((file) => {
      const isValidType = allowedTypes.includes(file.type) || 
        file.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|pdf|doc|docx)$/);
      const isValidSize = file.size <= maxSize;

      if (!isValidType) {
        invalidFiles.push(`${file.name} - Invalid file type. Only images, PDF, and DOCX are allowed.`);
      } else if (!isValidSize) {
        invalidFiles.push(`${file.name} - File size exceeds 5MB limit.`);
      }
    });

    if (invalidFiles.length > 0) {
      dispatch(
        openMessage({
          message: invalidFiles.join('; '),
          messageSeverity: "error",
        })
      );
      return;
    }

    setSelectedFiles((prev) => [...prev, ...files]);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAssigneeChange = (event) => {
    const value = event.target.value;
    const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
    
    setSelectedAssignees(selectedValues);
    
    const assignees = selectedValues.map(selectedValue => {
      const user = users.find(u => String(u.userId) === String(selectedValue));
      
      if (user) {
        return {
          userId: user.userId,
          email: user.id,
          name: user.display,
        };
      }
      return null;
    }).filter(Boolean);
    
    setFormData((prev) => ({
      ...prev,
      assignees,
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.sponsorId || !formData.fieldKey || !formData.title.trim()) {
      dispatch(
        openMessage({
          message: "Please fill in all required fields (Title is required)",
          messageSeverity: "error",
        })
      );
      return;
    }

    // Validate character limits
    if (formData.title.length > 255) {
      dispatch(
        openMessage({
          message: "Title must not exceed 255 characters",
          messageSeverity: "error",
        })
      );
      return;
    }

    if (formData.description.length > 1000) {
      dispatch(
        openMessage({
          message: "Description must not exceed 1000 characters",
          messageSeverity: "error",
        })
      );
      return;
    }

    try {
      const payload = {
        ...formData,
        metaData: JSON.stringify({
          stepName: stepName || stepKey || '',
          sectionName: sectionName || sectionKey || '',
          subSectionName: subSectionName || subSectionKey || '',
          categoryName: categoryName || categoryKey || '',
          fieldName: fieldLabel || fieldKey || '',
          participantId: subjectMasterId || '',
          createdByName: selfUser ? selfUser.display : '',
        }),
      };

      const result = await createQuery(payload).unwrap();

      if (result && result.id) {
        if (selectedFiles.length > 0 && uploadAttachments) {
          try {
            const formDataFiles = new FormData();
            selectedFiles.forEach((file) => {
              formDataFiles.append('files', file);
            });

            await uploadAttachments({
              queryId: result.id,
              formData: formDataFiles,
            }).unwrap();
          } catch (uploadError) {
            dispatch(
              openMessage({
                message: 'Query created but failed to upload some attachments',
                messageSeverity: 'warning',
              })
            );
          }
        }

        dispatch(
          openMessage({
            message: 'Query created successfully',
            messageSeverity: 'success',
          })
        );
        // Trigger refetch of queries in parent components
        dispatch(triggerRefetch());
        if (onQueryCreated) {
          onQueryCreated();
        }
        onBack();
      }
    } catch (error) {
      dispatch(
        openMessage({
          message: error?.data?.message || error?.message || 'Failed to create query',
          messageSeverity: 'error',
        })
      );
    }
  };

  return (
    <Box sx={{ height: '95%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, pt: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ minWidth: 'auto', px: 1 }}>
          Back
        </Button>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          pt: 2,
          flexGrow: 1,
          overflow: 'auto',
          pr: 1,
          minHeight: 0,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Title"
              value={formData.title}
              onChange={handleChange('title')}
              placeholder="Enter query title..."
              inputProps={{ maxLength: 255 }}
              helperText={isLoadingTag ? "Loading tag..." : `${formData.title.length}/255 characters`}
              error={formData.title.length > 255}
              InputProps={{
                startAdornment: formData.tag ? (
                  <InputAdornment position="start">
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        mr: 1,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        pr: 1,
                      }}
                    >
                      {formData.tag}
                    </Typography>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              multiline
              rows={4}
              placeholder="Enter query description (optional)..."
              inputProps={{ maxLength: 1000 }}
              helperText={`${formData.description.length}/1000 characters`}
              error={formData.description.length > 1000}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select 
                value={formData.priority} 
                onChange={handleChange('priority')} 
                label="Priority"
                renderValue={(selected) => {
                  const colors = getPriorityColor(selected);
                  return (
                    <Chip
                      label={selected === 'LOW' ? 'Low' : selected === 'MEDIUM' ? 'Medium' : 'High'}
                      size="small"
                      sx={{
                        backgroundColor: colors.bg,
                        color: colors.text,
                        fontWeight: 500,
                        height: 24,
                      }}
                    />
                  );
                }}
              >
                <MenuItem value="LOW">
                  <Chip
                    label="Low"
                    size="small"
                    sx={{
                      backgroundColor: getPriorityColor('LOW').bg,
                      color: getPriorityColor('LOW').text,
                      fontWeight: 500,
                    }}
                  />
                </MenuItem>
                <MenuItem value="MEDIUM">
                  <Chip
                    label="Medium"
                    size="small"
                    sx={{
                      backgroundColor: getPriorityColor('MEDIUM').bg,
                      color: getPriorityColor('MEDIUM').text,
                      fontWeight: 500,
                    }}
                  />
                </MenuItem>
                <MenuItem value="HIGH">
                  <Chip
                    label="High"
                    size="small"
                    sx={{
                      backgroundColor: getPriorityColor('HIGH').bg,
                      color: getPriorityColor('HIGH').text,
                      fontWeight: 500,
                    }}
                  />
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select 
                value={formData.status} 
                onChange={handleChange('status')} 
                label="Status"
                renderValue={(selected) => {
                  const colors = getStatusColor(selected);
                  const statusLabel = selected === 'OPEN' ? 'Open' : 
                                     selected === 'RESOLVED' ? 'Resolved' : 
                                     selected === 'VERIFIED' ? 'Verified' : 
                                     selected === 'REOPENED' ? 'Reopened' : selected;
                  return (
                    <Chip
                      label={statusLabel}
                      size="small"
                      sx={{
                        backgroundColor: colors.bg,
                        color: colors.text,
                        fontWeight: 500,
                        height: 24,
                      }}
                    />
                  );
                }}
              >
                <MenuItem value="OPEN">
                  <Chip
                    label="Open"
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor('OPEN').bg,
                      color: getStatusColor('OPEN').text,
                      fontWeight: 500,
                    }}
                  />
                </MenuItem>
                <MenuItem value="RESOLVED" disabled={true}>
                  <Chip
                    label="Resolved"
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor('RESOLVED').bg,
                      color: getStatusColor('RESOLVED').text,
                      fontWeight: 500,
                      opacity: 0.9,
                    }}
                  />
                </MenuItem>
                <MenuItem value="VERIFIED" disabled={true}>
                  <Chip
                    label="Verified"
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor('VERIFIED').bg,
                      color: getStatusColor('VERIFIED').text,
                      fontWeight: 500,
                      opacity: 0.9,
                    }}
                  />
                </MenuItem>
                <MenuItem value="REOPENED" disabled={true}>
                  <Chip
                    label="Reopened"
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor('REOPENED').bg,
                      color: getStatusColor('REOPENED').text,
                      fontWeight: 500,
                      opacity: 0.9,
                    }}
                  />
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Assignees</InputLabel>
              <Select
                multiple
                value={selectedAssignees || []}
                onChange={handleAssigneeChange}
                input={<OutlinedInput label="Assignees" />}
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return <em style={{ color: theme.palette.text.disabled }}>Select assignees</em>;
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((selectedValue) => {
                        const user = users.find(u => String(u.userId) === String(selectedValue));
                        return (
                          <Chip
                            key={selectedValue}
                            label={user ? (user.display || user.id) : selectedValue}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  );
                }}
              >
                {users.map((user) => {
                  const userIdentifier = user.userId;
                  const isSelected = Array.isArray(selectedAssignees) && selectedAssignees.length > 0 && selectedAssignees.some(
                    selected => String(selected) === String(userIdentifier)
                  );
                  return (
                    <MenuItem
                      key={userIdentifier}
                      value={userIdentifier}
                      sx={{
                        backgroundColor: isSelected ? theme.palette.action.selected : 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected 
                            ? theme.palette.action.selected 
                            : theme.palette.action.hover,
                        },
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        icon={icon}
                        checkedIcon={checkedIcon}
                        sx={{ mr: 1 }}
                      />
                      {user.display || user.id}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: selectedFiles.length > 0 ? 2 : 0 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="file-upload"
                  accept="image/*,.pdf,.doc,.docx"
                />
                <label htmlFor="file-upload">
                  <Button variant="outlined" component="span" startIcon={<AttachFileIcon />} size="small">
                    Attach Files
                  </Button>
                </label>
                <Typography variant="caption" color="text.secondary">
                  {selectedFiles.length > 0
                    ? `${selectedFiles.length} file(s) selected (max 10, 5MB each)`
                    : 'No files selected (Images, PDF, DOCX only - max 10 files, 5MB each)'}
                </Typography>
              </Box>
              {selectedFiles.length > 0 && (
                <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                  {selectedFiles.map((file, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveFile(index)} size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                      sx={{ py: 0.5 }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                            {file.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(file.size)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Button onClick={onBack} variant="outlined">
          Cancel
        </Button>
        <CustomButton type="submit" variant="contained" onClick={handleSubmit} disabled={isLoading || isUploading}>
          {isLoading || isUploading ? 'Creating...' : 'Create Query'}
        </CustomButton>
      </Box>
    </Box>
  );
};

export default React.memo(CreateQueryForm);

